import { Device } from "@/types/device";
import { NormalizedRelease } from "@/types/sofa";
import { compareVersions } from "@/utils/vulnerabilityMatching";

export interface HealthMetric { name: string; score: number; maxScore: number; weight: number; status: "excellent" | "good" | "fair" | "poor"; description: string; recommendations?: string[]; }
export interface DeviceHealthScore { overall: number; grade: "A+" | "A" | "B+" | "B" | "C+" | "C" | "D" | "F"; metrics: HealthMetric[]; lastUpdated: string; }
export interface HealthTrend { currentScore: number; previousScore: number; change: number; percentChange: number; trend: 'up' | 'down' | 'stable'; daysCompared: number; }
export interface HealthHistoryPoint { date: string; score: number; grade: string; metrics: HealthMetric[]; }

/**
 * Map device os_type to SOFA productName variants for matching.
 */
function getProductNames(osType: unknown): string[] {
  const ot = typeof osType === "string" ? osType.toLowerCase() : "";
  switch (ot) {
    case "macos": return ["macos"];
    case "ios": return ["ios"];
    case "ipados": return ["ios", "ipados"];
    case "tvos": return ["tvos"];
    case "watchos": return ["watchos"];
    case "visionos": return ["visionos"];
    default: return [ot];
  }
}

/**
 * Dynamically score OS compliance using SOFA feed data.
 * Finds the latest release for the device's platform, then scores based on
 * how far behind the device is and whether its version has actively exploited CVEs.
 */
export function calculateOSCompliance(device: Device, sofaReleases?: NormalizedRelease[]): HealthMetric {
  if (!device.os_version) {
    return { name: 'OS Compliance', score: 0, maxScore: 100, weight: 0.25, status: 'poor', description: 'OS version not available', recommendations: ['Update device'] };
  }

  const osType = device.os_type as string;
  const productNames = getProductNames(osType);

  // Filter SOFA releases to this platform
  const platformReleases = (Array.isArray(sofaReleases) ? sofaReleases : []).filter(r =>
    productNames.includes(typeof r?.productName === "string" ? r.productName.toLowerCase() : "")
  );

  // Sort by version descending to find latest and determine position
  const sortedVersions = [...new Set(platformReleases.map(r => r.productVersion).filter((v): v is string => typeof v === "string" && v.length > 0))]
    .sort((a, b) => compareVersions(b, a));

  const rawVersion = typeof device.os_version === "string" ? device.os_version : "";
  const deviceVersion = rawVersion.match(/(\d+[\d.]*)/)?.[1] || rawVersion;

  let score: number;
  let recommendations: string[] = [];

  if (sortedVersions.length === 0) {
    // No SOFA data available — generous default
    score = 75;
  } else {
    const latestVersion = sortedVersions[0];
    const cmp = compareVersions(deviceVersion, latestVersion);

    if (cmp >= 0) {
      // On latest or newer
      score = 100;
    } else {
      // Find how many versions behind
      const posIndex = sortedVersions.findIndex(v => compareVersions(deviceVersion, v) >= 0);
      const versionsBehind = posIndex === -1 ? sortedVersions.length : posIndex;

      // Base score: deduct per version behind
      score = Math.max(0, 100 - versionsBehind * 15);

      // Extra penalty if the device's current version has actively exploited CVEs
      const deviceRelease = platformReleases.find(r =>
        compareVersions(deviceVersion, r.productVersion) < 0
      );
      if (deviceRelease && deviceRelease.activelyExploitedCount > 0) {
        score = Math.max(0, score - 15);
        recommendations.push(`${deviceRelease.activelyExploitedCount} actively exploited CVE(s) affect this version`);
      }

      recommendations.push(`Update to ${latestVersion} (currently ${versionsBehind} release${versionsBehind > 1 ? 's' : ''} behind)`);
    }
  }

  const status: HealthMetric["status"] = score >= 90 ? "excellent" : score >= 75 ? "good" : score >= 50 ? "fair" : "poor";
  return {
    name: "OS Compliance",
    score,
    maxScore: 100,
    weight: 0.25,
    status,
    description: `Running ${device.os_type} ${device.os_version}`,
    ...(recommendations.length > 0 ? { recommendations } : {}),
  };
}

export function calculateSecurityPatches(device: Device): HealthMetric {
  const daysSince = Math.floor((Date.now() - new Date(device.last_seen).getTime()) / 86400000);
  let score = 100; if (daysSince > 7) score -= 20; if (daysSince > 14) score -= 30; if (daysSince > 30) score -= 40;
  score = Math.max(0, score);
  const status: HealthMetric["status"] = score >= 80 ? "excellent" : score >= 60 ? "good" : score >= 40 ? "fair" : "poor";
  return { name: "Security Patches", score, maxScore: 100, weight: 0.30, status, description: `Last check: ${daysSince} days ago` };
}

export function calculateEncryptionStatus(device: Device): HealthMetric {
  let score = 100; const status: HealthMetric["status"] = device.status === "pending" ? "fair" : "excellent";
  if (device.status === "pending") score = 50;
  return { name: "Encryption", score, maxScore: 100, weight: 0.25, status, description: "FileVault/Device encryption status" };
}

export function calculateBatteryHealth(device: Device): HealthMetric {
  let score = 100; let status: HealthMetric["status"] = "excellent";
  if (device.battery_level === null || device.battery_level === undefined) { score = 50; status = "fair"; }
  else if (device.battery_level < 20) { score = 40; status = "poor"; }
  else if (device.battery_level < 50) { score = 70; status = "fair"; }
  else if (device.battery_level < 80) { score = 85; status = "good"; }
  return { name: "Battery Health", score, maxScore: 100, weight: 0.10, status, description: device.battery_level ? `${device.battery_level}%` : "Unknown" };
}

export function calculateAppCompliance(device: Device): HealthMetric {
  const appCount = (device.installed_apps || []).length;
  let score = 100; let status: HealthMetric["status"] = "excellent";
  if (appCount === 0 && device.status !== "pending") { score = 30; status = "poor"; }
  return { name: "App Compliance", score, maxScore: 100, weight: 0.10, status, description: `${appCount} apps installed` };
}

export function calculateDeviceHealthScore(device: Device, sofaReleases?: NormalizedRelease[]): DeviceHealthScore {
  const metrics = [calculateOSCompliance(device, sofaReleases), calculateSecurityPatches(device), calculateEncryptionStatus(device), calculateBatteryHealth(device), calculateAppCompliance(device)];
  const overall = metrics.reduce((t, m) => t + m.score * m.weight, 0);
  let grade: DeviceHealthScore["grade"]; if (overall >= 95) grade = "A+"; else if (overall >= 90) grade = "A"; else if (overall >= 85) grade = "B+"; else if (overall >= 80) grade = "B"; else if (overall >= 75) grade = "C+"; else if (overall >= 70) grade = "C"; else if (overall >= 60) grade = "D"; else grade = "F";
  return { overall: Math.round(overall), grade, metrics, lastUpdated: new Date().toISOString() };
}

export function getHealthScoreColor(score: number): string { if (score >= 90) return "hsl(var(--chart-5))"; if (score >= 75) return "hsl(var(--chart-2))"; if (score >= 60) return "hsl(var(--chart-3))"; return "hsl(var(--chart-6))"; }
export function getStatusColor(status: HealthMetric["status"]): string { switch (status) { case "excellent": return "hsl(var(--chart-5))"; case "good": return "hsl(var(--chart-2))"; case "fair": return "hsl(var(--chart-3))"; case "poor": return "hsl(var(--chart-6))"; default: return "hsl(var(--muted-foreground))"; } }

// Stubbed — no backend API for health history
export async function saveDeviceHealthScore(_deviceId: string, _healthScore: DeviceHealthScore): Promise<void> {}
export async function getHealthScoreTrend(_deviceId: string, _daysBack: number = 7): Promise<HealthTrend | null> { return null; }
export async function getHealthHistory(_deviceId: string, _days: number = 30): Promise<HealthHistoryPoint[]> { return []; }
